import { useState, useRef, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  Bell,
  Clock,
  Calendar,
  AlertTriangle,
  MessageSquare,
  FileText,
  Users,
  CheckCheck,
} from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import { useAgencyProfile } from '@/hooks/useAgencyProfile'
import { useNotifications } from '@/hooks/useFirestore'
import { toDate, formatDate } from '@/lib/firebase/firestore'
import {
  markNotificationAsRead,
  markAllNotificationsAsRead,
} from '@/services/notifications'
import { Spinner } from '@/components/ui'
import { cn } from '@/lib/utils/cn'
import type { Notification } from '@/types'

export default function NotificationBell() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const { agency } = useAgencyProfile(user?.agencyId)
  const { data, isLoading, refetch } = useNotifications({ unreadOnly: false })
  const notifications = data as Notification[]

  const [isOpen, setIsOpen] = useState(false)
  const [isMarkingAll, setIsMarkingAll] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Lock background scroll when dropdown is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => { document.body.style.overflow = '' }
  }, [isOpen])

  const unreadNotifications = notifications.filter(n => !n.isRead && !n.isDismissed)
  const recentNotifications = notifications.filter(n => !n.isDismissed).slice(0, 5)
  const unreadCount = unreadNotifications.length

  const handleNotificationClick = async (notification: Notification) => {
    if (!notification.isRead && user) {
      await markNotificationAsRead(notification.id, user.agencyId)
      refetch()
    }

    setIsOpen(false)

    if (notification.actionUrl) {
      // Validate URL is a safe relative path before navigating
      const isSafeUrl = (url: string): boolean =>
        url.startsWith('/') && !url.startsWith('//') && !url.includes(':')
      if (isSafeUrl(notification.actionUrl)) {
        navigate(notification.actionUrl)
      }
    }
  }

  const handleMarkAllAsRead = async () => {
    if (!user) return

    setIsMarkingAll(true)
    try {
      await markAllNotificationsAsRead(user.uid, user.agencyId)
      refetch()
    } catch (error) {
      console.error('Error marking all as read:', error)
    } finally {
      setIsMarkingAll(false)
    }
  }

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          'relative p-2 rounded-lg transition-colors',
          isOpen
            ? 'bg-gray-100 text-gray-900 dark:bg-gray-800 dark:text-gray-100'
            : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-200'
        )}
      >
        <Bell className="h-5 w-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 h-5 w-5 flex items-center justify-center">
            <span className="absolute inline-flex h-full w-full rounded-full bg-primary-400 opacity-75 animate-ping" />
            <span className="relative inline-flex items-center justify-center h-4 w-4 rounded-full bg-primary-500 text-[10px] font-medium text-white">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          </span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute right-0 z-50 mt-2 w-80 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-lg dark:border-gray-800 dark:bg-gray-900 sm:w-96">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3 dark:border-gray-800">
            <h3 className="font-semibold text-gray-900 dark:text-gray-100">Notifications</h3>
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllAsRead}
                disabled={isMarkingAll}
                className="text-xs text-primary-600 hover:text-primary-700 flex items-center gap-1"
              >
                <CheckCheck className="h-3 w-3" />
                {isMarkingAll ? 'Marking...' : 'Mark all read'}
              </button>
            )}
          </div>

          {/* Content */}
          <div className="max-h-96 overflow-y-auto">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Spinner />
              </div>
            ) : recentNotifications.length === 0 ? (
              <div className="py-8 text-center">
                <Bell className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-500 dark:text-gray-400">No notifications</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100 dark:divide-gray-800">
                {recentNotifications.map(notification => (
                  <NotificationItem
                    key={notification.id}
                    notification={notification}
                    agencyTimeZone={agency?.timezone}
                    onClick={() => handleNotificationClick(notification)}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-gray-100 p-2 dark:border-gray-800">
            <Link
              to="/notifications"
              onClick={() => setIsOpen(false)}
              className="block w-full rounded-lg py-2 text-center text-sm text-primary-600 transition-colors hover:bg-primary-50 dark:hover:bg-primary-900/30"
            >
              View all notifications
            </Link>
          </div>
        </div>
      )}
    </div>
  )
}

function NotificationItem({
  notification,
  onClick,
  agencyTimeZone,
}: {
  notification: Notification
  onClick: () => void
  agencyTimeZone?: string
}) {
  const icon = getNotificationIcon(notification.type)
  const time = toDate(notification.createdAt)
  const notificationBody = notification.body || notification.message

  return (
    <button
      onClick={onClick}
      className={cn(
        'flex w-full items-start gap-3 p-4 text-left transition-colors hover:bg-gray-50 dark:hover:bg-gray-800',
        !notification.isRead && 'bg-primary-50/30 dark:bg-primary-900/20'
      )}
    >
      {/* Icon */}
      <div className="flex-shrink-0">{icon}</div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <p
          className={cn(
            'text-sm line-clamp-1',
            notification.isRead ? 'text-gray-600 dark:text-gray-300' : 'font-medium text-gray-900 dark:text-gray-100'
          )}
        >
          {notification.title}
        </p>
        {notificationBody && (
          <p className="mt-0.5 line-clamp-1 text-xs text-gray-500 dark:text-gray-400">
            {notificationBody}
          </p>
        )}
        {time && (
          <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
            {formatDate(time, 'datetime', agencyTimeZone)}
          </p>
        )}
      </div>

      {/* Unread dot */}
      {!notification.isRead && (
        <div className="w-2 h-2 rounded-full bg-primary-500 flex-shrink-0 mt-2" />
      )}
    </button>
  )
}

function getNotificationIcon(type: Notification['type']) {
  switch (type) {
    case 'visit_reminder':
      return <Clock className="h-5 w-5 text-blue-500" />
    case 'shift_open':
      return <Calendar className="h-5 w-5 text-green-500" />
    case 'incident_reported':
      return <AlertTriangle className="h-5 w-5 text-error-500" />
    case 'authorization_expiring':
      return <FileText className="h-5 w-5 text-warning-500" />
    case 'coverage_needed':
      return <Users className="h-5 w-5 text-orange-500" />
    case 'message':
      return <MessageSquare className="h-5 w-5 text-primary-500" />
    case 'poc_review':
      return <FileText className="h-5 w-5 text-purple-500" />
    default:
      return <Bell className="h-5 w-5 text-gray-500" />
  }
}
